import React, { useEffect } from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import WarningIcon from 'components/material-symbol/icons/Warning'
import VisibilityOffIcon from 'components/material-symbol/icons/VisibilityOff'
import ChatBubbleIcon from 'components/material-symbol/icons/ChatBubble'
import AddToPhotosIcon from 'components/material-symbol/icons/AddToPhotos'
import OpenInNewIcon from 'components/material-symbol/icons/OpenInNew'
import SchoolIcon from 'components/material-symbol/icons/School'
import RoomIcon from 'components/material-symbol/icons/Room'
import LockIcon from 'components/material-symbol/icons/Lock'
import { authenticatedRedirectLink } from 'lib/pga-dot-coach'
import { MARKETPLACE_LISTING_REASONS } from 'lib/coach-profile/marketplace-listing'
import {
  MARKETPLACE_LISTING_ACTIONS,
  trackMarketplaceListingBannerActionClicked,
  useIsMarketplaceTrackingAllowed,
} from './analytics'
import useStyles from './styles'

export const MARKETPLACE_BANNER_SOURCE = 'marketplace-banner'

const REASON_CONTENT = {
  [MARKETPLACE_LISTING_REASONS.ADM_INCOMPLETE]: {
    icon: SchoolIcon,
    title: "You haven't completed ADM training",
    description:
      'Complete ADM training to become eligible to promote your profile on PGA.com.',
    actions: [
      {
        id: MARKETPLACE_LISTING_ACTIONS.GO_TO_ADM_TRAINING,
        label: 'Go to ADM training',
        href: authenticatedRedirectLink('/trainingCenter/category/16873'),
      },
    ],
  },
  [MARKETPLACE_LISTING_REASONS.PROFILE_HIDDEN]: {
    icon: VisibilityOffIcon,
    title: 'Your profile is hidden',
    description: "Hidden profiles don't appear in PGA.com search results.",
    actions: [
      {
        id: MARKETPLACE_LISTING_ACTIONS.TURN_ON_VISIBILITY,
        label: 'Turn on visibility',
        to: '/pga-coach/profile',
      },
    ],
  },
  [MARKETPLACE_LISTING_REASONS.NO_VISIBLE_FACILITY]: {
    icon: RoomIcon,
    title: "You don't have a visible facility",
    description:
      'Golfers search by location. Make at least one facility visible on your profile.',
    actions: [
      {
        id: MARKETPLACE_LISTING_ACTIONS.MANAGE_FACILITIES,
        label: 'Manage facilities',
        to: '/pga-coach/settings/facilities',
      },
    ],
  },
  [MARKETPLACE_LISTING_REASONS.LEAD_COLLECTION_DISABLED]: {
    icon: ChatBubbleIcon,
    title: 'Your Contact button is turned off',
    description: "Golfers can't send you inquiries from your profile.",
    actions: [
      {
        id: MARKETPLACE_LISTING_ACTIONS.TURN_ON_CONTACT,
        label: 'Turn on Contact button',
        to: '/pga-coach/settings',
      },
    ],
  },
  [MARKETPLACE_LISTING_REASONS.LEAD_LIMIT_REACHED]: {
    icon: ChatBubbleIcon,
    title: "You've reached the free plan's lead limit",
    description:
      'Golfers can no longer contact you. Upgrade to PGA Coach+ for unlimited leads.',
  },
  [MARKETPLACE_LISTING_REASONS.NOT_BOOKABLE]: {
    icon: AddToPhotosIcon,
    title: 'Golfers have nothing to book',
    description:
      "You don't have any bookable offerings on PGA Coach, and you haven't linked an outside booking tool.",
    actions: [
      {
        id: MARKETPLACE_LISTING_ACTIONS.ADD_OFFERING,
        label: 'Add an offering',
        to: '/pga-coach/bookings/offerings',
      },
      {
        id: MARKETPLACE_LISTING_ACTIONS.USE_OWN_BOOKING_TOOL,
        label: 'Use my own booking tool',
        to: '/pga-coach/settings/booking-url',
      },
    ],
  },
  [MARKETPLACE_LISTING_REASONS.BOOKING_TYPES_PRIVATE]: {
    icon: LockIcon,
    title: 'Your booking types are all hidden',
    description:
      'Only you can book hidden booking types. Make at least one visible, or link the outside booking tool you already use.',
    actions: [
      {
        id: MARKETPLACE_LISTING_ACTIONS.MANAGE_BOOKING_TYPES,
        label: 'Manage booking types',
        to: '/pga-coach/bookings/booking-types',
      },
      {
        id: MARKETPLACE_LISTING_ACTIONS.USE_OWN_BOOKING_TOOL,
        label: 'Use my own booking tool',
        to: '/pga-coach/settings/booking-url',
      },
    ],
  },
}

const REACH_REASONS = [
  MARKETPLACE_LISTING_REASONS.LEAD_LIMIT_REACHED,
  MARKETPLACE_LISTING_REASONS.LEAD_COLLECTION_DISABLED,
  MARKETPLACE_LISTING_REASONS.NOT_BOOKABLE,
  MARKETPLACE_LISTING_REASONS.BOOKING_TYPES_PRIVATE,
]

const UpgradeButton = ({ className, onClick, loading }) => (
  <Button
    variant="contained"
    color="primary"
    size="small"
    className={className}
    onClick={onClick}
    disabled={loading}
    startIcon={<OpenInNewIcon fontSize="small" />}
  >
    Upgrade
  </Button>
)

const UpgradeAction = ({ className, onTrack, upgradeFlow }) => {
  const { trackPromptShown, handleUpgradeClick, upgradeLoading } = upgradeFlow

  useEffect(() => {
    trackPromptShown?.()
  }, [trackPromptShown])

  return (
    <UpgradeButton
      className={className}
      onClick={() => {
        onTrack(MARKETPLACE_LISTING_ACTIONS.UPGRADE)
        handleUpgradeClick({ source: MARKETPLACE_BANNER_SOURCE })
      }}
      loading={upgradeLoading}
    />
  )
}

const ReasonRow = ({ reason, classes, upgradeFlow }) => {
  const { pathname } = useLocation()
  const { icon: Icon, title, description, actions = [] } = REASON_CONTENT[
    reason
  ]
  const visibleActions = actions.filter(action => action.to !== pathname)
  const isTrackingAllowed = useIsMarketplaceTrackingAllowed()

  const trackAction = (action, destination) => {
    if (!isTrackingAllowed) return
    trackMarketplaceListingBannerActionClicked({ reason, action, destination })
  }

  return (
    <li className={classes.row} data-testid={`marketplace-reason-${reason}`}>
      <Box className={classes.rowIcon}>
        <Icon fontSize="inherit" />
      </Box>
      <Box className={classes.rowCopy}>
        <Typography className={classes.rowTitle}>{title}</Typography>
        <Typography className={classes.rowDescription}>
          {description}
        </Typography>
      </Box>
      <Box className={classes.rowActions}>
        {reason === MARKETPLACE_LISTING_REASONS.LEAD_LIMIT_REACHED ? (
          <UpgradeAction
            className={classes.actionButton}
            onTrack={trackAction}
            upgradeFlow={upgradeFlow}
          />
        ) : null}
        {visibleActions.map(action => (
          <Button
            key={action.label}
            variant="outlined"
            color="primary"
            size="small"
            {...(action.href
              ? {
                href: action.href,
                target: '_blank',
                rel: 'noopener noreferrer',
                endIcon: <OpenInNewIcon fontSize="small" />,
              }
              : { component: RouterLink, to: action.to })}
            onClick={() => trackAction(action.id, action.href ?? action.to)}
            className={classes.actionButton}
          >
            {action.label}
          </Button>
        ))}
      </Box>
    </li>
  )
}

const MarketplaceListingBanner = ({ reasons = [], className, upgradeFlow }) => {
  const classes = useStyles()

  if (reasons.length === 0) {
    return null
  }

  const profileReasons = reasons.filter(
    reason => !REACH_REASONS.includes(reason),
  )
  const reachReasons = reasons.filter(reason => REACH_REASONS.includes(reason))

  return (
    <Box
      component="section"
      className={className ? `${classes.banner} ${className}` : classes.banner}
      aria-labelledby="marketplace-listing-banner-title"
      data-testid="marketplace-listing-banner"
    >
      <Box className={classes.header}>
        <WarningIcon className={classes.headerIcon} fontSize="inherit" />
        <Box>
          <Typography
            component="h2"
            id="marketplace-listing-banner-title"
            className={classes.title}
          >
            Why am I not listed in the marketplace?
          </Typography>
          <Typography className={classes.caption}>
            Golfers can't find you on PGA.com right now. Here's what to fix.
          </Typography>
        </Box>
      </Box>

      {profileReasons.length > 0 ? (
        <ul className={classes.list}>
          {profileReasons.map(reason => (
            <ReasonRow
              key={reason}
              reason={reason}
              classes={classes}
              upgradeFlow={upgradeFlow}
            />
          ))}
        </ul>
      ) : null}

      {reachReasons.length > 0 ? (
        <>
          <Typography className={classes.groupLabel}>
            Golfers need a way to reach you. Fix at least one of these:
          </Typography>
          <ul className={classes.list}>
            {reachReasons.map(reason => (
              <ReasonRow
                key={reason}
                reason={reason}
                classes={classes}
                upgradeFlow={upgradeFlow}
              />
            ))}
          </ul>
        </>
      ) : null}
    </Box>
  )
}

export default MarketplaceListingBanner
